import React, { useState, useEffect, useCallback } from "react";
import { Patient } from "../../PatientCard";
import AppointmentService, {
  Appointment,
} from "../../../../services/appointmentService";

interface PatientAppointmentHistoryTabProps {
  patient: Patient;
}

const PatientAppointmentHistoryTab: React.FC<
  PatientAppointmentHistoryTabProps
> = ({ patient }) => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [selectedAppointment, setSelectedAppointment] =
    useState<Appointment | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  // Check if screen is mobile/tablet
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);
  // Add this handler function after your existing handlers (around line 60)
  const handleViewInCalendar = (appointment: Appointment) => {
    const appointmentDate = appointment.appointment_date;
    const appointmentId = appointment.id;
    const doctorId = appointment.doctor_id;

    // Add view parameter to force day view
    const calendarUrl = `/appointments?date=${appointmentDate}&highlight=${appointmentId}&doctor=${doctorId}&view=day`;

    window.location.href = calendarUrl;
  };
  const loadAppointments = useCallback(async () => {
    if (!patient.id) return;

    setIsLoading(true);
    setError(null);
    try {
      // ✅ FIXED: Pass patient_id to get ALL appointments for this patient (no date filter)
      const result = await AppointmentService.getAppointments({
        patient_id: patient.id, // Backend will filter by patient, but no date restriction
      });

      // The backend should already filter by patient_id, but keep client-side filter as backup
      const patientAppointments = (result.data || []).filter(
        (appointment) => appointment.patient_id === patient.id
      );

      // Sort by appointment date (newest first)
      patientAppointments.sort(
        (a, b) =>
          new Date(b.appointment_date).getTime() -
          new Date(a.appointment_date).getTime()
      );

      setAppointments(patientAppointments);
    } catch (err: any) {
      setError(err.message || "Failed to load appointment history");
    } finally {
      setIsLoading(false);
    }
  }, [patient.id]);

  useEffect(() => {
    loadAppointments();
  }, [loadAppointments, refreshTrigger]);

  const handleNewAppointment = () => {
    // Navigate to appointments page with patient pre-filled
    const url = `/appointments?bookForPatient=${patient.id}`;
    window.location.href = url;
  };

  const calculateStats = () => {
    const completed = appointments.filter((apt) => apt.status === "completed");
    const scheduled = appointments.filter((apt) => apt.status === "scheduled");
    const confirmed = appointments.filter((apt) => apt.status === "confirmed");
    const cancelled = appointments.filter((apt) => apt.status === "cancelled");
    const noShow = appointments.filter((apt) => apt.status === "no_show");

    // Find next appointment
    const upcoming = appointments
      .filter(
        (apt) =>
          ["scheduled", "confirmed"].includes(apt.status) &&
          new Date(apt.appointment_date) >= new Date()
      )
      .sort(
        (a, b) =>
          new Date(a.appointment_date).getTime() -
          new Date(b.appointment_date).getTime()
      );

    // Find last appointment
    const lastCompleted = completed.length > 0 ? completed[0] : null;

    return {
      total: appointments.length,
      completed: completed.length,
      scheduled: scheduled.length,
      confirmed: confirmed.length,
      cancelled: cancelled.length,
      noShow: noShow.length,
      upcoming: upcoming.length,
      nextAppointment: upcoming[0] || null,
      lastAppointment: lastCompleted,
    };
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case "completed":
        return "#10b981";
      case "confirmed":
        return "#10b981";
      case "scheduled":
        return "#3b82f6";
      case "arrived":
        return "#8b5cf6";
      case "cancelled":
        return "#ef4444";
      case "no_show":
        return "#f59e0b";
      default:
        return "#6b7280";
    }
  };

  const getStatusIcon = (status: string): string => {
    switch (status) {
      case "completed":
        return "✅";
      case "confirmed":
        return "✅";
      case "scheduled":
        return "📅";
      case "arrived":
        return "🏥";
      case "cancelled":
        return "❌";
      case "no_show":
        return "👻";
      default:
        return "📋";
    }
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case "completed":
        return "Completed";
      case "confirmed":
        return "Confirmed";
      case "scheduled":
        return "Scheduled";
      case "arrived":
        return "Arrived";
      case "cancelled":
        return "Cancelled";
      case "no_show":
        return "No Show";
      default:
        return status;
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatTime = (timeString: string): string => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const containerStyle: React.CSSProperties = {
    padding: isMobile ? "16px" : "24px",
    height: "100%",
    overflow: "auto",
    maxWidth: "100%",
  };

  const headerStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    justifyContent: "space-between",
    alignItems: isMobile ? "stretch" : "center",
    gap: isMobile ? "16px" : "0",
    marginBottom: "24px",
    padding: isMobile ? "16px" : "20px",
    background: "#ffffff",
    borderRadius: "12px",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    border: "1px solid #e5e7eb",
  };

  const buttonStyle = (
    variant: "primary" | "secondary" = "primary"
  ): React.CSSProperties => ({
    padding: isMobile ? "14px 20px" : "12px 24px",
    borderRadius: "8px",
    border: "none",
    fontSize: "14px",
    fontWeight: "500",
    cursor: "pointer",
    transition: "all 0.2s ease",
    background: variant === "primary" ? "#10b981" : "#e5e7eb",
    color: variant === "primary" ? "white" : "#374151",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    width: isMobile ? "100%" : "auto",
    textAlign: "center" as const,
  });

  const mainContentStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "row",
    // flexDirection: isMobile || selectedAppointment ? "column" : "row",
    overflow: "hidden",
    gap: isMobile ? "20px" : "0",
  };

  const sidebarStyle: React.CSSProperties = {
    width: isMobile ? "100%" : selectedAppointment ? "40%" : "100%",
    borderRight:
      !isMobile && selectedAppointment ? "1px solid #e5e7eb" : "none",
    borderBottom:
      isMobile && selectedAppointment ? "1px solid #e5e7eb" : "none",
    overflow: "auto",
    padding: isMobile ? "0" : "24px",
    transition: "width 0.3s ease",
  };

  const detailsStyle: React.CSSProperties = {
    width: isMobile ? "100%" : "60%",
    overflow: "auto",
    padding: isMobile ? "20px 0 0 0" : "24px",
    background: isMobile ? "#ffffff" : "#f8fafc",
    borderRadius: isMobile ? "12px" : "0",
    border: isMobile ? "1px solid #e5e7eb" : "none",
  };

  const appointmentCardStyle: React.CSSProperties = {
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    padding: isMobile ? "14px" : "16px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    background: "#ffffff",
    marginBottom: "12px",
  };

  const statsStyle: React.CSSProperties = {
    background: "#ffffff",
    borderRadius: "12px",
    padding: isMobile ? "16px" : "20px",
    marginBottom: "24px",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    border: "1px solid #e5e7eb",
  };

  const statsGridStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: isMobile
      ? "repeat(2, 1fr)"
      : "repeat(auto-fit, minmax(150px, 1fr))",
    gap: isMobile ? "12px" : "16px",
    marginBottom: "16px",
  };

  const nextLastGridStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
    gap: "16px",
    marginTop: "16px",
    paddingTop: "16px",
    borderTop: "1px solid #e5e7eb",
  };

  const detailsGridStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: isMobile
      ? "1fr"
      : "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "16px",
    marginBottom: "16px",
  };

  const infoGridStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: isMobile
      ? "1fr"
      : "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "12px",
    fontSize: "12px",
    color: "#166534",
  };

  const stats = calculateStats();

  return (
    <div style={containerStyle}>
      {/* Header with Action Button */}
      <div style={headerStyle}>
        <div style={{ flex: 1 }}>
          <h3
            style={{
              fontSize: isMobile ? "18px" : "20px",
              fontWeight: "600",
              margin: 0,
              marginBottom: "4px",
              color: "#1f2937",
            }}
          >
            📅 Appointment History
          </h3>
          <p
            style={{
              fontSize: "14px",
              color: "#6b7280",
              margin: 0,
            }}
          >
            All appointments for{" "}
            {patient.full_name || `${patient.first_name} ${patient.last_name}`}
          </p>
        </div>

        <div
          style={{
            display: "flex",
            gap: "12px",
            width: isMobile ? "100%" : "auto",
          }}
        >
          <button
            style={buttonStyle("primary")}
            onClick={handleNewAppointment}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#059669")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "#10b981")}
          >
            📅 Book New Appointment
          </button>
        </div>
      </div>

      {/* Appointment Statistics */}
      <div style={statsStyle}>
        <h4
          style={{
            fontSize: "16px",
            fontWeight: "600",
            marginBottom: "16px",
            color: "#1f2937",
          }}
        >
          📈 Appointment Overview
        </h4>

        <div style={statsGridStyle} className="no">
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                fontSize: isMobile ? "20px" : "24px",
                fontWeight: "700",
                color: "#10b981",
                marginBottom: "4px",
              }}
            >
              {stats.total}
            </div>
            <div style={{ fontSize: "12px", color: "#6b7280" }}>Total</div>
          </div>

          <div style={{ textAlign: "center" }}>
            <div
              style={{
                fontSize: isMobile ? "20px" : "24px",
                fontWeight: "700",
                color: "#10b981",
                marginBottom: "4px",
              }}
            >
              {stats.completed}
            </div>
            <div style={{ fontSize: "12px", color: "#6b7280" }}>Completed</div>
          </div>

          <div style={{ textAlign: "center" }}>
            <div
              style={{
                fontSize: isMobile ? "20px" : "24px",
                fontWeight: "700",
                color: "#3b82f6",
                marginBottom: "4px",
              }}
            >
              {stats.upcoming}
            </div>
            <div style={{ fontSize: "12px", color: "#6b7280" }}>Upcoming</div>
          </div>

          <div style={{ textAlign: "center" }}>
            <div
              style={{
                fontSize: isMobile ? "20px" : "24px",
                fontWeight: "700",
                color: "#ef4444",
                marginBottom: "4px",
              }}
            >
              {stats.cancelled}
            </div>
            <div style={{ fontSize: "12px", color: "#6b7280" }}>Cancelled</div>
          </div>

          {!isMobile && (
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  color: "#f59e0b",
                  marginBottom: "4px",
                }}
              >
                {stats.noShow}
              </div>
              <div style={{ fontSize: "12px", color: "#6b7280" }}>No Shows</div>
            </div>
          )}
        </div>

        {/* Next/Last Appointment Info */}
        <div style={nextLastGridStyle} className="no">
          <div className="no">
            <div
              style={{
                fontSize: "12px",
                fontWeight: "600",
                color: "#6b7280",
                marginBottom: "4px",
                textTransform: "uppercase",
              }}
            >
              Next Appointment
            </div>
            <div style={{ fontSize: "14px", color: "#1f2937" }}>
              {stats.nextAppointment ? (
                <>
                  {formatDate(stats.nextAppointment.appointment_date)} at{" "}
                  {formatTime(stats.nextAppointment.appointment_time)}
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#6b7280",
                      marginTop: "2px",
                    }}
                  >
                    Dr. {stats.nextAppointment.doctor?.first_name}{" "}
                    {stats.nextAppointment.doctor?.last_name}
                  </div>
                </>
              ) : (
                <span style={{ color: "#6b7280" }}>
                  No upcoming appointments
                </span>
              )}
            </div>
          </div>

          <div>
            <div
              style={{
                fontSize: "12px",
                fontWeight: "600",
                color: "#6b7280",
                marginBottom: "4px",
                textTransform: "uppercase",
              }}
            >
              Last Visit
            </div>
            <div style={{ fontSize: "14px", color: "#1f2937" }}>
              {stats.lastAppointment ? (
                <>
                  {formatDate(stats.lastAppointment.appointment_date)} at{" "}
                  {formatTime(stats.lastAppointment.appointment_time)}
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#6b7280",
                      marginTop: "2px",
                    }}
                  >
                    Dr. {stats.lastAppointment.doctor?.first_name}{" "}
                    {stats.lastAppointment.doctor?.last_name}
                  </div>
                </>
              ) : (
                <span style={{ color: "#6b7280" }}>
                  No completed appointments
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div
        style={{
          background: "#ffffff",
          borderRadius: "12px",
          padding: isMobile ? "16px" : "20px",
          marginBottom: "24px",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
          border: "1px solid #e5e7eb",
        }}
      >
        <div style={mainContentStyle}>
          {/* Appointments List */}
          <div style={sidebarStyle}>
            {isLoading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "200px",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    border: "4px solid #e2e8f0",
                    borderTop: "4px solid #10b981",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                  }}
                />
                Loading appointment history...
              </div>
            ) : error ? (
              <div
                style={{
                  textAlign: "center",
                  color: "#ef4444",
                  padding: "40px 20px",
                  fontSize: "16px",
                }}
              >
                ⚠️ {error}
                <br />
                <button
                  onClick={loadAppointments}
                  style={{ ...buttonStyle("primary"), marginTop: "16px" }}
                >
                  Retry
                </button>
              </div>
            ) : appointments.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  color: "#6b7280",
                  padding: "40px 20px",
                  fontSize: "16px",
                }}
              >
                📅 No appointments found for this patient
                <br />
                <button
                  onClick={handleNewAppointment}
                  style={{ ...buttonStyle("primary"), marginTop: "16px" }}
                >
                  📅 Book First Appointment
                </button>
              </div>
            ) : (
              <div>
                <h3
                  style={{
                    fontSize: "16px",
                    fontWeight: "600",
                    marginBottom: "16px",
                    color: "#1f2937",
                  }}
                >
                  Appointment History ({appointments.length})
                </h3>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  {appointments.map((appointment) => (
                    <div
                      key={appointment.id}
                      onClick={() => setSelectedAppointment(appointment)}
                      style={{
                        ...appointmentCardStyle,
                        border:
                          selectedAppointment?.id === appointment.id
                            ? "2px solid #10b981"
                            : "1px solid #e5e7eb",
                        background:
                          selectedAppointment?.id === appointment.id
                            ? "#f0fdf4"
                            : "#ffffff",
                      }}
                      onMouseEnter={(e) => {
                        if (selectedAppointment?.id !== appointment.id) {
                          e.currentTarget.style.borderColor = "#10b981";
                          e.currentTarget.style.background = "#f9fafb";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedAppointment?.id !== appointment.id) {
                          e.currentTarget.style.borderColor = "#e5e7eb";
                          e.currentTarget.style.background = "#ffffff";
                        }
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          marginBottom: "8px",
                          flexDirection: isMobile ? "column" : "row",
                          gap: isMobile ? "8px" : "0",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              marginBottom: "4px",
                              flexWrap: "wrap",
                            }}
                          >
                            <span
                              style={{
                                padding: "2px 6px",
                                borderRadius: "8px",
                                fontSize: "10px",
                                fontWeight: "500",
                                background:
                                  getStatusColor(appointment.status) + "20",
                                color: getStatusColor(appointment.status),
                                whiteSpace: "nowrap",
                              }}
                            >
                              {getStatusIcon(appointment.status)}{" "}
                              {getStatusLabel(appointment.status)}
                            </span>
                            <span
                              style={{
                                fontSize: "12px",
                                color: "#6b7280",
                              }}
                            >
                              {appointment.type_label}
                            </span>
                          </div>

                          <h4
                            style={{
                              fontSize: "14px",
                              fontWeight: "600",
                              margin: 0,
                              color: "#1f2937",
                              marginBottom: "4px",
                            }}
                          >
                            Dr. {appointment.doctor?.first_name}{" "}
                            {appointment.doctor?.last_name}
                          </h4>

                          {appointment.doctor?.specialty && (
                            <div
                              style={{
                                fontSize: "12px",
                                color: "#6b7280",
                                marginBottom: "8px",
                              }}
                            >
                              {appointment.doctor.specialty}
                            </div>
                          )}
                        </div>
                      </div>

                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: isMobile
                            ? "1fr 1fr"
                            : "1fr 1fr 1fr",
                          gap: "8px",
                          fontSize: "12px",
                          color: "#6b7280",
                          marginBottom: "8px",
                        }}
                      >
                        <span>{formatDate(appointment.appointment_date)}</span>
                        <span>{formatTime(appointment.appointment_time)}</span>
                        {!isMobile && <span>{appointment.duration} min</span>}
                      </div>

                      {appointment.reason && (
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#6b7280",
                            marginTop: "8px",
                            fontStyle: "italic",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical" as any,
                          }}
                        >
                          "{appointment.reason}"
                        </div>
                      )}

                      {/* Custom Pricing Indicator */}
                      {appointment.has_custom_pricing && (
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#f59e0b",
                            marginTop: "4px",
                            fontWeight: "500",
                          }}
                        >
                          💰 Custom pricing: $
                          {appointment.amount_to_collect || 0}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Appointment Details Panel */}
          {selectedAppointment && (
            <div style={detailsStyle}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: "24px",
                  flexDirection: isMobile ? "column" : "row",
                  gap: isMobile ? "12px" : "0",
                }}
              >
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: isMobile ? "12px" : "0",
                    }}
                  >
                    <h3
                      style={{
                        fontSize: isMobile ? "18px" : "20px",
                        fontWeight: "600",
                        margin: 0,
                        marginBottom: "8px",
                        color: "#1f2937",
                      }}
                    >
                      Appointment Details
                    </h3>
                    {/* ADD: View in Calendar Button */}
                    <button
                      onClick={() => handleViewInCalendar(selectedAppointment)}
                      style={{
                        ...buttonStyle("secondary"),
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        fontSize: "13px",
                        padding: "8px 12px",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "#e5e7eb";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "#f3f4f6";
                      }}
                    >
                      📅 View in Calendar
                    </button>
                    {isMobile && (
                      <button
                        onClick={() => setSelectedAppointment(null)}
                        style={{
                          background: "#f3f4f6",
                          border: "1px solid #e5e7eb",
                          borderRadius: "6px",
                          padding: "6px 10px",
                          fontSize: "12px",
                          cursor: "pointer",
                          color: "#6b7280",
                        }}
                      >
                        ← Back to List
                      </button>
                    )}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      alignItems: "center",
                      marginBottom: "8px",
                      flexWrap: "wrap",
                    }}
                  >
                    <span
                      style={{
                        padding: "4px 8px",
                        borderRadius: "12px",
                        fontSize: "12px",
                        fontWeight: "500",
                        background:
                          getStatusColor(selectedAppointment.status) + "20",
                        color: getStatusColor(selectedAppointment.status),
                      }}
                    >
                      {getStatusIcon(selectedAppointment.status)}{" "}
                      {getStatusLabel(selectedAppointment.status)}
                    </span>
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#6b7280",
                      }}
                    >
                      {selectedAppointment.type_label}
                    </span>
                  </div>
                </div>
              </div>

              {/* Appointment Information */}
              <div
                style={{
                  background: "#ffffff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  padding: "16px",
                  marginBottom: "20px",
                }}
              >
                <div style={detailsGridStyle}>
                  <div>
                    <div
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#6b7280",
                        marginBottom: "4px",
                        textTransform: "uppercase",
                      }}
                    >
                      Doctor
                    </div>
                    <div style={{ fontSize: "14px", color: "#1f2937" }}>
                      Dr. {selectedAppointment.doctor?.first_name}{" "}
                      {selectedAppointment.doctor?.last_name}
                      {selectedAppointment.doctor?.specialty && (
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#6b7280",
                            marginTop: "2px",
                          }}
                        >
                          {selectedAppointment.doctor.specialty}
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <div
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#6b7280",
                        marginBottom: "4px",
                        textTransform: "uppercase",
                      }}
                    >
                      Date & Time
                    </div>
                    <div style={{ fontSize: "14px", color: "#1f2937" }}>
                      {formatDate(selectedAppointment.appointment_date)}
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#6b7280",
                          marginTop: "2px",
                        }}
                      >
                        {formatTime(selectedAppointment.appointment_time)} (
                        {selectedAppointment.duration} minutes)
                      </div>
                    </div>
                  </div>
                </div>

                {/* Custom Pricing Information */}
                {selectedAppointment.has_custom_pricing && (
                  <div
                    style={{
                      background: "#fef3c7",
                      color: "#92400e",
                      padding: "16px",
                      borderRadius: "8px",
                      fontSize: "14px",
                      marginTop: "16px",
                      border: "1px solid #fbbf24",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: "600",
                        marginBottom: "8px",
                        fontSize: "16px",
                      }}
                    >
                      💰 Custom Pricing Applied
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        opacity: 0.9,
                        marginBottom: "4px",
                      }}
                    >
                      Amount Collected: $
                      {selectedAppointment.amount_to_collect || 0}
                    </div>
                    {selectedAppointment.original_total_fee && (
                      <div
                        style={{
                          fontSize: "12px",
                          opacity: 0.9,
                          marginBottom: "4px",
                        }}
                      >
                        Original Total: $
                        {selectedAppointment.original_total_fee}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Appointment Notes */}
              {selectedAppointment.reason && (
                <div
                  style={{
                    background: "#ffffff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    padding: "16px",
                    marginBottom: "20px",
                  }}
                >
                  <h4
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      marginBottom: "8px",
                      color: "#1f2937",
                    }}
                  >
                    📝 Reason for Visit
                  </h4>
                  <p
                    style={{
                      fontSize: "13px",
                      color: "#4b5563",
                      margin: 0,
                      lineHeight: "1.5",
                      wordWrap: "break-word",
                    }}
                  >
                    {selectedAppointment.reason}
                  </p>
                </div>
              )}

              {selectedAppointment.patient_notes && (
                <div
                  style={{
                    background: "#ffffff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    padding: "16px",
                    marginBottom: "20px",
                  }}
                >
                  <h4
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      marginBottom: "8px",
                      color: "#1f2937",
                    }}
                  >
                    👤 Patient Notes
                  </h4>
                  <p
                    style={{
                      fontSize: "13px",
                      color: "#4b5563",
                      margin: 0,
                      lineHeight: "1.5",
                      wordWrap: "break-word",
                    }}
                  >
                    {selectedAppointment.patient_notes}
                  </p>
                </div>
              )}

              {selectedAppointment.notes && (
                <div
                  style={{
                    background: "#ffffff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    padding: "16px",
                    marginBottom: "20px",
                  }}
                >
                  <h4
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      marginBottom: "8px",
                      color: "#1f2937",
                    }}
                  >
                    🩺 Clinical Notes
                  </h4>
                  <p
                    style={{
                      fontSize: "13px",
                      color: "#4b5563",
                      margin: 0,
                      lineHeight: "1.5",
                      wordWrap: "break-word",
                    }}
                  >
                    {selectedAppointment.notes}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Information Panel */}
      {/* <div
        style={{
          background: "#f0fdf4",
          border: "1px solid #bbf7d0",
          borderRadius: "8px",
          padding: "16px",
          marginTop: "24px",
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: "20px", marginBottom: "8px" }}>📅</div>
        <h4
          style={{
            fontSize: "16px",
            fontWeight: "600",
            marginBottom: "8px",
            color: "#166534",
          }}
        >
          Complete Appointment History
        </h4>
        <p
          style={{
            fontSize: "14px",
            color: "#166534",
            marginBottom: "16px",
            lineHeight: "1.5",
          }}
        >
          View all past and upcoming appointments for this patient. To schedule
          a new appointment, click the "Book New Appointment" button which will
          take you to the appointment scheduling system.
        </p>
        <div style={infoGridStyle}>
          <div>✅ Complete History View</div>
          <div>✅ Status Tracking</div>
          <div>✅ Custom Pricing Display</div>
          <div>✅ Quick Booking Access</div>
          <div>✅ Doctor Information</div>
          <div>✅ Appointment Details</div>
        </div>
      </div> */}

      <style>
        {`
         @keyframes spin {
           0% { transform: rotate(0deg); }
           100% { transform: rotate(360deg); }
         }
         
         button:hover:not(:disabled) {
           transform: translateY(-1px);
           box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
         }

         @media (max-width: 768px) {
           .appointment-card {
             margin-bottom: 8px !important;
           }
           
           .stats-grid {
             grid-template-columns: repeat(2, 1fr) !important;
           }
           
           .details-grid {
             grid-template-columns: 1fr !important;
           }
         }

         @media (max-width: 480px) {
           .container {
             padding: 12px !important;
           }
           
           .header {
             padding: 12px !important;
           }
           
           .stats-container {
             padding: 12px !important;
           }
         }
       `}
      </style>
    </div>
  );
};

export default PatientAppointmentHistoryTab;
